import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Card,
  CardContent,
  Fab,
  Slide,
  Zoom,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Badge,
  Tooltip,
  Snackbar,
  Alert,
  Pagination,
  InputAdornment,
  Stack
} from '@mui/material';
import {
  Phone as PhoneIcon,
  PhoneCallback as PhoneCallbackIcon,
  PhonePaused as PhonePausedIcon,
  PhoneDisabled as PhoneDisabledIcon,
  CallEnd as CallEndIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  TransferWithinAStation as TransferIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  RecordVoiceOver as RecordIcon,
  Stop as StopIcon,
  CallMade as CallMadeIcon,
  CallReceived as CallReceivedIcon,
  CallMissed as CallMissedIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Search as SearchIcon,
  PhoneInTalk as PhoneInTalkIcon,
  Assignment as AssignmentIcon,
  Star as StarIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Today as TodayIcon,
  DateRange as DateRangeIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import AsteriskService from '../../services/AsteriskService';
import asteriskConfig from '../../config/asterisk';

// Styled components for call interface
const CallContainer = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 20,
  right: 20,
  width: 350,
  minHeight: 200,
  zIndex: 9999,
  background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)',
  color: 'white',
  borderRadius: 16,
  overflow: 'hidden',
  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
  '& .pulse': {
    animation: 'pulse 1.5s infinite',
  },
  '@keyframes pulse': {
    '0%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.5,
    },
    '100%': {
      opacity: 1,
    },
  },
}));

const CallButton = styled(IconButton)(({ theme, variant }) => ({
  width: 56,
  height: 56,
  margin: theme.spacing(0.5),
  background: variant === 'answer' ? '#4caf50' : 
              variant === 'hangup' ? '#f44336' :
              variant === 'hold' ? '#ff9800' : 'rgba(255,255,255,0.2)',
  color: 'white',
  '&:hover': {
    background: variant === 'answer' ? '#388e3c' : 
                variant === 'hangup' ? '#d32f2f' :
                variant === 'hold' ? '#f57c00' : 'rgba(255,255,255,0.3)',
  },
}));

const CallStatusIndicator = styled(Box)(({ status }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '4px 12px',
  borderRadius: 20,
  background: status === 'connected' ? '#4caf50' :
              status === 'ringing' ? '#ff9800' :
              status === 'hold' ? '#2196f3' :
              status === 'transferring' ? '#9c27b0' : '#666',
  fontSize: '0.875rem',
  fontWeight: 500
}));

const AsteriskCallManager = ({ customerDatabase = [], onCustomerFound = null }) => {
  // State management
  const [asteriskService] = useState(() => new AsteriskService(asteriskConfig.asterisk));

  const [currentCall, setCurrentCall] = useState(null);
  const [callHistory, setCallHistory] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [callStatus, setCallStatus] = useState('idle');
  const [callDuration, setCallDuration] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isHeld, setIsHeld] = useState(false);
  const [volume, setVolume] = useState(75);
  const [isRingtonePlaying, setIsRingtonePlaying] = useState(false);
  
  // UI State
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferNumber, setTransferNumber] = useState('');
  const [showCallHistory, setShowCallHistory] = useState(false);
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  // Call History Filtering and Pagination
  const [historySearchQuery, setHistorySearchQuery] = useState('');
  const [selectedCallType, setSelectedCallType] = useState('all');
  const [selectedCallStatus, setSelectedCallStatus] = useState('all');
  const [selectedRecording, setSelectedRecording] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Refs
  const callTimer = useRef(null);
  const audioRef = useRef(null);

  // Ringtone functions
  const playRingtone = useCallback(() => {
    if (audioRef.current && !isRingtonePlaying) {
      audioRef.current.currentTime = 0;
      audioRef.current.loop = true;
      audioRef.current.volume = volume / 100;
      audioRef.current.play().catch(error => {
        console.error('Error playing ringtone:', error);
      });
      setIsRingtonePlaying(true);
    }
  }, [volume, isRingtonePlaying]);

  const stopRingtone = useCallback(() => {
    if (audioRef.current && isRingtonePlaying) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsRingtonePlaying(false);
    }
  }, [isRingtonePlaying]);

  // Initialize Asterisk connection
  useEffect(() => {
    // Set up demo data for testing
    const demoCallHistory = [
      {
        id: 'CALL001',
        name: 'রহিম উদ্দিন',
        number: '01712345678',
        type: 'incoming',
        duration: 185,
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
        status: 'completed',
        recording: true
      },
      {
        id: 'CALL002',
        name: 'ফাতেমা খাতুন',
        number: '01823456789',
        type: 'outgoing',
        duration: 92,
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000), // 4 hours ago
        status: 'completed',
        recording: false
      },
      {
        id: 'CALL003',
        number: '01934567890',
        type: 'missed',
        duration: 0,
        timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000), // 6 hours ago
        status: 'missed',
        recording: false
      },
      {
        id: 'CALL004',
        name: 'করিম মিয়া',
        number: '01645678901',
        type: 'incoming',
        duration: 234,
        timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000), // 8 hours ago
        status: 'completed',
        recording: true
      },
      {
        id: 'CALL005',
        name: 'সালমা বেগম',
        number: '01756789012',
        type: 'outgoing',
        duration: 156,
        timestamp: new Date(Date.now() - 10 * 60 * 60 * 1000), // 10 hours ago
        status: 'completed',
        recording: false
      },
      // Additional calls for full history (yesterday)
      {
        id: 'CALL006',
        name: 'আব্দুল হক',
        number: '01887654321',
        type: 'incoming',
        duration: 298,
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
        status: 'completed',
        recording: true
      },
      {
        id: 'CALL007',
        number: '01998765432',
        type: 'missed',
        duration: 0,
        timestamp: new Date(Date.now() - 26 * 60 * 60 * 1000), // 1 day ago
        status: 'missed',
        recording: false
      },
      {
        id: 'CALL008',
        name: 'নাসির আহমেদ',
        number: '01776543210',
        type: 'outgoing',
        duration: 432,
        timestamp: new Date(Date.now() - 28 * 60 * 60 * 1000), // 1 day ago
        status: 'completed',
        recording: true
      },
      {
        id: 'CALL009',
        name: 'রোকেয়া খাতুন',
        number: '01665432109',
        type: 'incoming',
        duration: 167,
        timestamp: new Date(Date.now() - 30 * 60 * 60 * 1000), // 1.25 days ago
        status: 'completed',
        recording: false
      },
      {
        id: 'CALL010',
        name: 'জামাল উদ্দিন',
        number: '01554321098',
        type: 'outgoing',
        duration: 89,
        timestamp: new Date(Date.now() - 32 * 60 * 60 * 1000), // 1.33 days ago
        status: 'completed',
        recording: false
      },
      // More calls from 2 days ago
      {
        id: 'CALL011',
        number: '01443210987',
        type: 'missed',
        duration: 0,
        timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000), // 2 days ago
        status: 'missed',
        recording: false
      },
      {
        id: 'CALL012',
        name: 'শাহিনা বেগম',
        number: '01332109876',
        type: 'incoming',
        duration: 345,
        timestamp: new Date(Date.now() - 50 * 60 * 60 * 1000), // 2 days ago
        status: 'completed',
        recording: true
      },
      {
        id: 'CALL013',
        name: 'মোহাম্মদ আলী',
        number: '01221098765',
        type: 'outgoing',
        duration: 123,
        timestamp: new Date(Date.now() - 52 * 60 * 60 * 1000), // 2 days ago
        status: 'completed',
        recording: false
      },
      {
        id: 'CALL014',
        name: 'হাসিনা আক্তার',
        number: '01110987654',
        type: 'incoming',
        duration: 278,
        timestamp: new Date(Date.now() - 54 * 60 * 60 * 1000), // 2.25 days ago
        status: 'completed',
        recording: true
      },
      {
        id: 'CALL015',
        number: '01009876543',
        type: 'missed',
        duration: 0,
        timestamp: new Date(Date.now() - 56 * 60 * 60 * 1000), // 2.33 days ago
        status: 'missed',
        recording: false
      },
      // Calls from 3 days ago
      {
        id: 'CALL016',
        name: 'তাহমিনা খাতুন',
        number: '01798765432',
        type: 'incoming',
        duration: 456,
        timestamp: new Date(Date.now() - 72 * 60 * 60 * 1000), // 3 days ago
        status: 'completed',
        recording: true
      },
      {
        id: 'CALL017',
        name: 'রফিক উল্লাহ',
        number: '01687654321',
        type: 'outgoing',
        duration: 234,
        timestamp: new Date(Date.now() - 74 * 60 * 60 * 1000), // 3 days ago
        status: 'completed',
        recording: false
      },
      {
        id: 'CALL018',
        name: 'সাবিনা ইয়াসমিন',
        number: '01576543210',
        type: 'incoming',
        duration: 189,
        timestamp: new Date(Date.now() - 76 * 60 * 60 * 1000), // 3 days ago
        status: 'completed',
        recording: true
      },
      {
        id: 'CALL019',
        number: '01465432109',
        type: 'missed',
        duration: 0,
        timestamp: new Date(Date.now() - 78 * 60 * 60 * 1000), // 3.25 days ago
        status: 'missed',
        recording: false
      },
      {
        id: 'CALL020',
        name: 'আনিসুর রহমান',
        number: '01354321098',
        type: 'outgoing',
        duration: 167,
        timestamp: new Date(Date.now() - 80 * 60 * 60 * 1000), // 3.33 days ago
        status: 'completed',
        recording: false
      }
    ];

    // Set demo data
    setCallHistory(demoCallHistory);
    
    asteriskService.connect();
    
    // Set up event handlers
    asteriskService.on('connected', () => {
      setIsConnected(true);
      setNotificationMessage('Asterisk সংযোগ স্থাপিত হয়েছে');
      setShowNotification(true);
    });

    asteriskService.on('incomingCall', handleIncomingCall);
    asteriskService.on('outgoingCall', handleOutgoingCall);
    asteriskService.on('callAnswered', handleCallAnswered);
    asteriskService.on('callConnected', handleCallConnected);
    asteriskService.on('callEnded', handleCallEnded);
    asteriskService.on('callHeld', handleCallHeld);
    asteriskService.on('callTransferred', handleCallTransferred);

    // Test event listener for demo purposes
    const handleTestCall = (event) => {
      handleIncomingCall(event.detail);
    };
    
    const handleMakeCall = (event) => {
      // Directly start the call with dialing animation
      const number = event.detail.number || '';
      if (number) {
        // Create outgoing call data
        const outgoingCallData = {
          callId: 'outgoing-' + Date.now(),
          callerNumber: number,
          callerName: 'আউটগোয়িং কল',
          callerLocation: 'কল করা হচ্ছে...'
        };
        
        // Start the call directly
        handleOutgoingCall(outgoingCallData);
        makeCall(number);
      }
    };
    
    window.addEventListener('test-incoming-call', handleTestCall);
    window.addEventListener('make-outgoing-call', handleMakeCall);

    // Load call history
    loadCallHistory();

    return () => {
      if (callTimer.current) {
        clearInterval(callTimer.current);
      }
      window.removeEventListener('test-incoming-call', handleTestCall);
      window.removeEventListener('make-outgoing-call', handleMakeCall);
      asteriskService.disconnect();
    };
  }, []);

  // Call event handlers
  const handleIncomingCall = useCallback((callData) => {
    setCurrentCall({
      ...callData,
      direction: 'incoming',
      status: 'ringing'
    });
    setCallStatus('ringing');
    
    // Find customer information
    const customer = findCustomerByNumber(callData.callerNumber);
    if (customer) {
      setCustomerInfo(customer);
      if (onCustomerFound) {
        onCustomerFound(customer);
      }
    }

    // Play ringtone (in real app)
    console.log('Incoming call from:', callData.callerNumber);
    playRingtone();
  }, [customerDatabase, onCustomerFound, playRingtone]);

  const handleOutgoingCall = useCallback((callData) => {
    setCurrentCall({
      ...callData,
      direction: 'outgoing',
      status: 'connecting'
    });
    setCallStatus('connecting');
  }, []);

  const startCallTimer = useCallback(() => {
    callTimer.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  }, []);

  const handleCallAnswered = useCallback((data) => {
    setCallStatus('connected');
    startCallTimer();
    if (currentCall) {
      setCurrentCall(prev => ({ ...prev, status: 'connected' }));
    }
    stopRingtone();
  }, [currentCall, stopRingtone, startCallTimer]);

  const handleCallConnected = useCallback((data) => {
    setCallStatus('connected');
    startCallTimer();
    if (currentCall) {
      setCurrentCall(prev => ({ ...prev, status: 'connected' }));
    }
    stopRingtone();
  }, [currentCall, stopRingtone, startCallTimer]);

  const handleCallEnded = useCallback((data) => {
    if (currentCall) {
      // Add to call history
      const callRecord = {
        ...currentCall,
        duration: callDuration,
        endTime: new Date(),
        status: 'completed'
      };
      
      setCallHistory(prev => [callRecord, ...prev]);
    }

    // Reset call state
    setCurrentCall(null);
    setCallStatus('idle');
    setCallDuration(0);
    setIsRecording(false);
    setIsHeld(false);
    setCustomerInfo(null);
    
    if (callTimer.current) {
      clearInterval(callTimer.current);
    }
    stopRingtone();
  }, [currentCall, callDuration, stopRingtone]);

  const handleCallHeld = useCallback((data) => {
    setIsHeld(true);
    setCallStatus('hold');
  }, []);

  const handleCallTransferred = useCallback((data) => {
    setNotificationMessage(`কল ট্রান্সফার করা হয়েছে ${data.targetNumber} এ`);
    setShowNotification(true);
    handleCallEnded(data);
  }, [handleCallEnded]);

  // Helper functions
  const findCustomerByNumber = (number) => {
    return customerDatabase.find(customer => 
      customer.phone === number || 
      customer.phone.replace(/\D/g, '') === number.replace(/\D/g, '')
    );
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const loadCallHistory = async () => {
    try {
      const history = await asteriskService.getCallHistory();
      setCallHistory(history);
    } catch (error) {
      console.error('Error loading call history:', error);
    }
  };

  // Call control functions
  const answerCall = async () => {
    if (currentCall) {
      await asteriskService.answerCall(currentCall.callId);
    }
  };

  const hangupCall = async () => {
    if (currentCall) {
      await asteriskService.hangupCall(currentCall.callId);
    }
    stopRingtone();
  };

  const holdCall = async () => {
    if (currentCall) {
      await asteriskService.holdCall(currentCall.callId);
    }
  };

  const makeCall = async (number) => {
    try {
      await asteriskService.makeCall(number);
    } catch (error) {
      console.error('Error making call:', error);
    }
  };

  const transferCall = async () => {
    if (currentCall && transferNumber) {
      try {
        await asteriskService.transferCall(currentCall.callId, transferNumber);
        setShowTransferDialog(false);
        setTransferNumber('');
      } catch (error) {
        console.error('Error transferring call:', error);
      }
    }
  };

  const toggleRecording = async () => {
    if (currentCall) {
      try {
        if (isRecording) {
          await asteriskService.stopRecording(currentCall.callId);
        } else {
          await asteriskService.startRecording(currentCall.callId);
        }
        setIsRecording(!isRecording);
      } catch (error) {
        console.error('Error toggling recording:', error);
      }
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    // In real implementation, mute/unmute microphone
  };

  const handleCloseCallHistory = () => {
    setShowCallHistory(false);
    setShowFullHistory(false);
    // Reset filters and pagination when closing
    setTimeout(() => {
      resetFilters();
    }, 300); // Small delay to prevent flicker
  };

  // Call History Filtering and Pagination Logic
  const filterCallHistory = () => {
    let filtered = [...callHistory];

    // Search filter
    if (historySearchQuery.trim()) {
      const query = historySearchQuery.toLowerCase();
      filtered = filtered.filter(call => 
        (call.name && call.name.toLowerCase().includes(query)) ||
        call.number.includes(query)
      );
    }

    // Call type filter
    if (selectedCallType !== 'all') {
      filtered = filtered.filter(call => call.type === selectedCallType);
    }

    // Call status filter
    if (selectedCallStatus !== 'all') {
      filtered = filtered.filter(call => call.status === selectedCallStatus);
    }

    // Recording filter
    if (selectedRecording !== 'all') {
      if (selectedRecording === 'recorded') {
        filtered = filtered.filter(call => call.recording === true);
      } else {
        filtered = filtered.filter(call => call.recording === false);
      }
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(call => {
        const callDate = new Date(call.timestamp);
        
        switch (dateFilter) {
          case 'today':
            return callDate >= today;
          case 'yesterday':
            const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
            return callDate >= yesterday && callDate < today;
          case 'last7days':
            const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
            return callDate >= weekAgo;
          case 'last30days':
            const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
            return callDate >= monthAgo;
          default:
            return true;
        }
      });
    }

    return filtered;
  };

  const getPaginatedCalls = () => {
    const filtered = filterCallHistory();
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return {
      calls: filtered.slice(startIndex, endIndex),
      total: filtered.length,
      totalPages: Math.ceil(filtered.length / pageSize)
    };
  };

  const resetFilters = () => {
    setHistorySearchQuery('');
    setSelectedCallType('all');
    setSelectedCallStatus('all');
    setSelectedRecording('all');
    setDateFilter('all');
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  return (
    <>
      {/* Connection Status Indicator */}
      <Fab 
        size="small"
        color={isConnected ? "success" : "default"}
        sx={{ 
          position: 'fixed', 
          top: 100, 
          right: 20,
          zIndex: 9997,
          opacity: 0.8
        }}
        title={isConnected ? "Call Manager Connected" : "Call Manager Disconnected"}
      >
        {isConnected ? <PhoneIcon /> : <PhoneDisabledIcon />}
      </Fab>

      {/* Call Status Indicator - Always show call history */}
      <Fab 
        color="primary" 
        sx={{ 
          position: 'fixed', 
          bottom: 20, 
          left: 20,
          zIndex: 9998 
        }}
        onClick={() => setShowCallHistory(true)}
        title="কল হিস্ট্রি দেখুন"
      >
        <Badge 
          badgeContent={callHistory.filter(call => call.type === 'missed').length} 
          color="error"
        >
          <HistoryIcon />
        </Badge>
      </Fab>

      {/* Active Call Interface */}
      {currentCall && (
        <Slide direction="up" in={Boolean(currentCall)} mountOnEnter unmountOnExit>
          <CallContainer>
            {/* Call Header */}
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <CallStatusIndicator status={callStatus}>
                {callStatus === 'ringing' && <PhoneCallbackIcon sx={{ fontSize: '1rem' }} />}
                {callStatus === 'connected' && <PhoneInTalkIcon sx={{ fontSize: '1rem' }} />}
                {callStatus === 'hold' && <PauseIcon sx={{ fontSize: '1rem' }} />}
                {callStatus === 'connecting' && <PhoneIcon sx={{ fontSize: '1rem' }} />}
                
                {callStatus === 'ringing' && 'ইনকামিং কল'}
                {callStatus === 'connected' && 'কল চলছে'}
                {callStatus === 'hold' && 'হোল্ডে আছে'}
                {callStatus === 'connecting' && 'সংযোগ হচ্ছে'}
              </CallStatusIndicator>

              <Avatar 
                sx={{ 
                  width: 80, 
                  height: 80, 
                  mx: 'auto', 
                  mt: 2, 
                  mb: 1,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  fontSize: '2rem'
                }}
              >
                {currentCall.callerName ? currentCall.callerName.charAt(0) : 
                 currentCall.targetNumber ? currentCall.targetNumber.charAt(0) : 'U'}
              </Avatar>

              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {currentCall.callerName || currentCall.targetNumber || 'অজানা'}
              </Typography>
              
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {currentCall.callerNumber || currentCall.targetNumber}
              </Typography>

              {callStatus === 'connected' && (
                <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                  {formatDuration(callDuration)}
                </Typography>
              )}

              {currentCall.callerLocation && (
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  📍 {currentCall.callerLocation}
                </Typography>
              )}
            </Box>

            {/* Customer Information */}
            {customerInfo && (
              <Box sx={{ px: 2, pb: 2 }}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.1)', color: 'white' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    গ্রাহকের তথ্য
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    📞 {customerInfo.phone}<br/>
                    📍 {customerInfo.address}<br/>
                    🛒 মোট অর্ডার: {customerInfo.totalOrders}<br/>
                    💰 মোট খরচ: ৳{customerInfo.totalSpent?.toLocaleString()}
                  </Typography>
                </Paper>
              </Box>
            )}

            {/* Call Controls */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              px: 2,
              pb: 2,
              gap: 1
            }}>
              {callStatus === 'ringing' && currentCall.direction === 'incoming' && (
                <>
                  <CallButton variant="answer" onClick={answerCall}>
                    <PhoneIcon />
                  </CallButton>
                  <CallButton variant="hangup" onClick={hangupCall}>
                    <CallEndIcon />
                  </CallButton>
                </>
              )}

              {callStatus === 'connected' && (
                <>
                  <Tooltip title={isMuted ? 'মাইক চালু করুন' : 'মাইক বন্ধ করুন'}>
                    <CallButton onClick={toggleMute}>
                      {isMuted ? <MicOffIcon /> : <MicIcon />}
                    </CallButton>
                  </Tooltip>

                  <Tooltip title={isHeld ? 'কল চালু করুন' : 'কল হোল্ড করুন'}>
                    <CallButton variant="hold" onClick={holdCall}>
                      {isHeld ? <PlayIcon /> : <PauseIcon />}
                    </CallButton>
                  </Tooltip>

                  <Tooltip title="কল ট্রান্সফার">
                    <CallButton onClick={() => setShowTransferDialog(true)}>
                      <TransferIcon />
                    </CallButton>
                  </Tooltip>

                  <Tooltip title={isRecording ? 'রেকর্ডিং বন্ধ' : 'রেকর্ডিং শুরু'}>
                    <CallButton onClick={toggleRecording}>
                      {isRecording ? <StopIcon /> : <RecordIcon />}
                    </CallButton>
                  </Tooltip>

                  <CallButton variant="hangup" onClick={hangupCall}>
                    <CallEndIcon />
                  </CallButton>
                </>
              )}

              {(callStatus === 'connecting' || callStatus === 'hold') && (
                <CallButton variant="hangup" onClick={hangupCall}>
                  <CallEndIcon />
                </CallButton>
              )}
            </Box>

            {/* Recording Indicator */}
            {isRecording && (
              <Box sx={{ 
                position: 'absolute', 
                top: 10, 
                right: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: '#f44336',
                px: 1,
                py: 0.5,
                borderRadius: 1
              }}>
                <RecordIcon sx={{ fontSize: '0.9rem' }} />
                <Typography variant="caption">REC</Typography>
              </Box>
            )}

            {/* Ringtone Playing Indicator */}
            {isRingtonePlaying && callStatus === 'ringing' && (
              <Box sx={{ 
                position: 'absolute', 
                top: 10, 
                left: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: '#ff9800',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                animation: 'pulse 1.5s infinite'
              }}>
                <VolumeUpIcon sx={{ fontSize: '0.9rem' }} />
                <Typography variant="caption">RING</Typography>
              </Box>
            )}
          </CallContainer>
        </Slide>
      )}

      {/* Transfer Call Dialog */}
      <Dialog 
        open={showTransferDialog} 
        onClose={() => setShowTransferDialog(false)}
      >
        <DialogTitle>কল ট্রান্সফার</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="ট্রান্সফার করার নম্বর"
            fullWidth
            variant="outlined"
            value={transferNumber}
            onChange={(e) => setTransferNumber(e.target.value)}
            placeholder="01XXXXXXXXX বা Extension"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTransferDialog(false)}>বাতিল</Button>
          <Button 
            onClick={transferCall} 
            variant="contained"
            disabled={!transferNumber}
          >
            ট্রান্সফার করুন
          </Button>
        </DialogActions>
      </Dialog>

      {/* Call History Dialog */}
      <Dialog 
        open={showCallHistory} 
        onClose={handleCloseCallHistory}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon />
            {showFullHistory ? 'সম্পূর্ণ কল হিস্ট্রি' : 'সাম্প্রতিক কল হিস্ট্রি'}
            <Chip 
              label={showFullHistory ? `${getPaginatedCalls().total} কল` : `শেষ ${Math.min(callHistory.length, 10)} কল`}
              size="small"
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                '& .MuiChip-label': { fontWeight: 600 }
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => setShowFullHistory(!showFullHistory)}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                color: 'white',
                fontWeight: 600
              }}
            >
              {showFullHistory ? 'সাম্প্রতিক দেখুন' : 'ফুল হিস্টোরি'}
            </Button>
            <IconButton
              color="inherit"
              onClick={() => {
                window.dispatchEvent(new CustomEvent('make-outgoing-call', {
                  detail: { number: '' }
                }));
              }}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}
            >
              <AddIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Filtering Section - Only show in full history mode */}
          {showFullHistory && (
            <Box sx={{ mt: 2, mb: 3, p: 3, bgcolor: '#f5f5f5', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <FilterListIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  ফিল্টার অপশন
                </Typography>
                <Button
                  startIcon={<ClearIcon />}
                  onClick={resetFilters}
                  size="small"
                  sx={{ ml: 'auto' }}
                >
                  সব রিসেট করুন
                </Button>
              </Box>
              
              <Grid container spacing={2}>
                {/* Search */}
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="নাম বা নম্বর খুঁজুন..."
                    value={historySearchQuery}
                    onChange={(e) => setHistorySearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Call Type Filter */}
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>কল টাইপ</InputLabel>
                    <Select
                      value={selectedCallType}
                      label="কল টাইপ"
                      onChange={(e) => setSelectedCallType(e.target.value)}
                    >
                      <MenuItem value="all">সব ধরনের</MenuItem>
                      <MenuItem value="incoming">ইনকামিং</MenuItem>
                      <MenuItem value="outgoing">আউটগোয়িং</MenuItem>
                      <MenuItem value="missed">মিসড</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Status Filter */}
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>স্ট্যাটাস</InputLabel>
                    <Select
                      value={selectedCallStatus}
                      label="স্ট্যাটাস"
                      onChange={(e) => setSelectedCallStatus(e.target.value)}
                    >
                      <MenuItem value="all">সব স্ট্যাটাস</MenuItem>
                      <MenuItem value="completed">সম্পন্ন</MenuItem>
                      <MenuItem value="missed">মিসড</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Recording Filter */}
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>রেকর্ডিং</InputLabel>
                    <Select
                      value={selectedRecording}
                      label="রেকর্ডিং"
                      onChange={(e) => setSelectedRecording(e.target.value)}
                    >
                      <MenuItem value="all">সব কল</MenuItem>
                      <MenuItem value="recorded">রেকর্ডেড</MenuItem>
                      <MenuItem value="not-recorded">রেকর্ডেড নয়</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Date Filter */}
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>তারিখ</InputLabel>
                    <Select
                      value={dateFilter}
                      label="তারিখ"
                      onChange={(e) => setDateFilter(e.target.value)}
                    >
                      <MenuItem value="all">সব তারিখ</MenuItem>
                      <MenuItem value="today">আজ</MenuItem>
                      <MenuItem value="yesterday">গতকাল</MenuItem>
                      <MenuItem value="last7days">শেষ ৭ দিন</MenuItem>
                      <MenuItem value="last30days">শেষ ৩০ দিন</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Summary Stats */}
          {showFullHistory && (
            <Box sx={{ mt: 2, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e8' }}>
                    <Typography variant="h4" color="success.main">
                      {filterCallHistory().filter(call => call.status === 'completed').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      সম্পন্ন কল
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#ffebee' }}>
                    <Typography variant="h4" color="error.main">
                      {filterCallHistory().filter(call => call.type === 'missed').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      মিসড কল
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                    <Typography variant="h4" color="primary.main">
                      {Math.floor(filterCallHistory().filter(call => call.duration > 0).reduce((total, call) => total + call.duration, 0) / 60)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      মোট মিনিট
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f3e5f5' }}>
                    <Typography variant="h4" color="primary.main">
                      {filterCallHistory().filter(call => call.recording).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      রেকর্ডেড কল
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Page Size Selector - Only show in full history mode */}
          {showFullHistory && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                মোট {getPaginatedCalls().total} টি কল পাওয়া গেছে
              </Typography>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>প্রতি পাতায়</InputLabel>
                <Select
                  value={pageSize}
                  label="প্রতি পাতায়"
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                >
                  <MenuItem value={10}>১০ টি</MenuItem>
                  <MenuItem value={25}>২৫ টি</MenuItem>
                  <MenuItem value={50}>৫০ টি</MenuItem>
                  <MenuItem value={100}>১০০ টি</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          <TableContainer sx={{ mt: 2, maxHeight: showFullHistory ? 500 : 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>নম্বর/নাম</TableCell>
                  <TableCell align="center">ধরণ</TableCell>
                  <TableCell align="center">সময়কাল</TableCell>
                  <TableCell align="center">তারিখ</TableCell>
                  <TableCell align="center">স্ট্যাটাস</TableCell>
                  <TableCell align="center">কার্যক্রম</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(showFullHistory ? getPaginatedCalls().calls : callHistory.slice(0, 10)).map((call) => (
                  <TableRow key={call.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ 
                          width: 32, 
                          height: 32, 
                          bgcolor: call.type === 'incoming' ? 'success.main' :
                                   call.type === 'outgoing' ? 'primary.main' : 'error.main', 
                          fontSize: '0.9rem' 
                        }}>
                          {call.name ? call.name.charAt(0) : call.number.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {call.name || call.number}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {call.number}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {call.type === 'incoming' && <CallReceivedIcon color="success" />}
                      {call.type === 'outgoing' && <CallMadeIcon color="primary" />}
                      {call.type === 'missed' && <CallMissedIcon color="error" />}
                    </TableCell>
                    <TableCell align="center">
                      {call.duration > 0 ? formatDuration(call.duration) : '--'}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption">
                        {call.timestamp.toLocaleDateString('bn-BD')}
                        <br />
                        {call.timestamp.toLocaleTimeString('bn-BD', { 
                          hour: '2-digit', 
                          minute: '2-digit'
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={call.status === 'completed' ? 'সম্পন্ন' : 
                              call.status === 'missed' ? 'মিসড' : call.status}
                        color={call.status === 'completed' ? 'success' : 
                               call.status === 'missed' ? 'error' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        size="small" 
                        onClick={() => makeCall(call.number)}
                        title="কল ব্যাক"
                      >
                        <PhoneCallbackIcon />
                      </IconButton>
                      {call.recording && (
                        <IconButton 
                          size="small" 
                          title="রেকর্ডিং শুনুন"
                        >
                          <VolumeUpIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination - Only show in full history mode */}
          {showFullHistory && getPaginatedCalls().totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Stack spacing={2}>
                <Pagination
                  count={getPaginatedCalls().totalPages}
                  page={currentPage}
                  onChange={(event, page) => handlePageChange(page)}
                  color="primary"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                />
                <Typography variant="caption" color="text.secondary" textAlign="center">
                  পাতা {currentPage} / {getPaginatedCalls().totalPages} - মোট {getPaginatedCalls().total} টি কল
                </Typography>
              </Stack>
            </Box>
          )}

          {!showFullHistory && callHistory.length > 10 && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                আরও {callHistory.length - 10} টি কল আছে
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCallHistory}>বন্ধ করুন</Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={showNotification}
        autoHideDuration={4000}
        onClose={() => setShowNotification(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowNotification(false)} 
          severity="success"
          variant="filled"
        >
          {notificationMessage}
        </Alert>
      </Snackbar>

      {/* Ringtone Audio Element */}
      <audio
        ref={audioRef}
        preload="auto"
        style={{ display: 'none' }}
        onLoadStart={() => console.log('Audio loading started')}
        onCanPlay={() => console.log('Audio can play')}
        onError={(e) => console.error('Audio error:', e)}
        onEnded={() => setIsRingtonePlaying(false)}
      >
        <source src="/sounds/ringtone.mp3" type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>
    </>
  );
};

export default AsteriskCallManager; 